import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Utils from '../utils';
import * as types from '../../store/constants';
import { LoanFormatIcon } from '../common/GildIcon';
import $ from '../utils/query';
import { usePreviousProps } from '../../hooks/usePreviousProps';
import { DropDownMenu } from '../common/DropDownMenu';
import styled from 'styled-components';
import FormatCurrency from '../utils/formatCurrency';
import { useTranslation } from 'react-i18next';
import { useSelectionLists } from '../../api/selection-list/useSelectionLists';

const StSelectionListName = styled.span`
    font-weight: bold;
    display: block;
`;

const StSelectionListItem = styled.li`
    margin-bottom: 8px;
`;

export const SelectionListInfo = () => {
    const { t } = useTranslation();
    const { activeList, selectionLists } = useSelectionLists();
    const [expanded, setExpanded] = useState(false);

    const quickSLRef = useSelectionListAnimation();

    if (!activeList) {
        return null;
    }

    return (
        <div
            className="quick-sl-container display-flex"
            ref={quickSLRef}
            data-displayname="SelectionlistInfo"
            aria-label="Selection list"
        >
            <i className="material-icons md-upper sl-icon">content_paste</i>
            <Link
                to={`${types.PATH_SELECTIONLISTS}/${activeList.id}`}
                className="quick-sl"
                data-testid="selection-list-detail-link"
            >
                <div>{Utils.truncate(activeList.listName)}</div>
                <div className="display-flex quick-sl-totals">
                    {(activeList.ebooksAmount ?? 0) > 0 ||
                    (activeList.eaudiobooksAmount ?? 0) > 0 ||
                    (activeList.epressAmount ?? 0) > 0 ? (
                        <>
                            {(activeList.ebooksAmount ?? 0) > 0 ? (
                                <span className="display-inline-flex">
                                    <LoanFormatIcon icon="ebook-filled" className="loanformat-icon-base" />
                                    <span>
                                        {t('numberFormat.numberWithoutDecimalPoint', {
                                            val: activeList.ebooksAmount
                                        })}
                                    </span>
                                </span>
                            ) : null}
                            {(activeList.eaudiobooksAmount ?? 0) > 0 ? (
                                <span className="display-inline-flex">
                                    <LoanFormatIcon icon="eaudiobook-filled" className="loanformat-icon-base" />
                                    <span>
                                        {t('numberFormat.numberWithoutDecimalPoint', {
                                            val: activeList.eaudiobooksAmount
                                        })}
                                    </span>
                                </span>
                            ) : null}
                            {(activeList.epressAmount ?? 0) > 0 ? (
                                <span className="display-inline-flex gild">
                                    <LoanFormatIcon icon="emagazine-filled" className="loanformat-icon-base" />
                                    <span>
                                        {t('numberFormat.numberWithoutDecimalPoint', {
                                            val: activeList.epressAmount
                                        })}
                                    </span>
                                </span>
                            ) : null}
                        </>
                    ) : null}
                    <span className="display-inline-flex">
                        {t('numberFormat.numberWithCurrencySymbol', {
                            val: FormatCurrency.convertIntegerToCurrencyFormat(activeList.totalNetValue),
                            currencySymbol: FormatCurrency.mapCurrencyToSymbol(activeList.currency)
                        })}
                    </span>
                </div>
            </Link>
            <DropDownMenu
                expanded={expanded}
                gap={10}
                align="left"
                onToggleClick={setExpanded}
                onClickOutside={setExpanded}
            >
                {selectionLists.map(list => (
                    <StSelectionListItem key={`selectionList-${list.id}`}>
                        <Link
                            to={`${types.PATH_SELECTIONLISTS}/${list.id}`}
                            onClick={() => setExpanded(false)}
                            className={`display-block ${list.id === activeList.id ? 'active' : ''}`}
                        >
                            <span className="display-block">
                                <StSelectionListName>{Utils.truncate(list.listName)}</StSelectionListName>
                                <span className="display-flex">
                                    <span className="marker-badge">
                                        {t('numberFormat.numberWithCurrencySymbol', {
                                            val: FormatCurrency.convertIntegerToCurrencyFormat(list.totalNetValue),
                                            currencySymbol: FormatCurrency.mapCurrencyToSymbol(list.currency)
                                        })}
                                    </span>
                                    {list.eaudiobooksAmount ? (
                                        <span className="marker-badge display-inline-flex">
                                            <LoanFormatIcon
                                                icon="eaudiobook-filled"
                                                className="loanformat-icon-small"
                                            />
                                            <span>
                                                {t('numberFormat.numberWithoutDecimalPoint', {
                                                    val: list.eaudiobooksAmount
                                                })}
                                            </span>
                                        </span>
                                    ) : null}
                                    {list.ebooksAmount ? (
                                        <span className="marker-badge display-inline-flex">
                                            <LoanFormatIcon icon="ebook-filled" className="loanformat-icon-small" />
                                            <span>
                                                {t('numberFormat.numberWithoutDecimalPoint', {
                                                    val: list.ebooksAmount
                                                })}
                                            </span>
                                        </span>
                                    ) : null}
                                    {list.epressAmount ? (
                                        <span className="marker-badge display-inline-flex gild">
                                            <LoanFormatIcon icon="emagazine-filled" className="loanformat-icon-small" />
                                            <span>
                                                {t('numberFormat.numberWithoutDecimalPoint', {
                                                    val: list.epressAmount
                                                })}
                                            </span>
                                        </span>
                                    ) : null}
                                </span>
                                {list.lastModifiedAt ? (
                                    <span className="display-block small">
                                        {t('marketing.productGroups.lastEdited', {
                                            val: list.lastModifiedAt
                                        })}
                                    </span>
                                ) : null}
                            </span>
                        </Link>
                    </StSelectionListItem>
                ))}
            </DropDownMenu>
        </div>
    );
};

const useSelectionListAnimation = () => {
    const { activeList } = useSelectionLists();
    const prevActiveList = usePreviousProps(activeList);
    const quickSLRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const hasListChanged = prevActiveList?.id && prevActiveList.id !== activeList?.id;
        const hasListUpdated =
            prevActiveList?.lastModifiedAt && prevActiveList.lastModifiedAt !== activeList?.lastModifiedAt;

        if (hasListChanged || hasListUpdated) {
            if (document.getElementById('animatedQuickSelectionList')) return;
            const quickSelectionList = document.querySelector('.quick-sl-container');
            const clonedQuickSelectionList = quickSelectionList?.cloneNode(true) as HTMLElement;
            const originalQuickSelectionListRect = quickSelectionList?.getBoundingClientRect();
            const quickSlInner = $('.quick-sl')[0];

            const scrollPos = window.scrollY || window.pageYOffset;
            if (!clonedQuickSelectionList || !originalQuickSelectionListRect) return;
            clonedQuickSelectionList.style.top = `${originalQuickSelectionListRect.top + scrollPos}px`;
            clonedQuickSelectionList.style.left = `${originalQuickSelectionListRect.left}px`;
            (clonedQuickSelectionList.firstChild! as HTMLElement).style.position = 'initial';
            $('.header')[0].appendChild(clonedQuickSelectionList);

            const initialDisplay = quickSLRef.current?.style.display;
            if (quickSLRef.current) {
                quickSLRef.current.style.display = 'none';
            }
            clonedQuickSelectionList.classList.add('target-container');

            let timeoutFunc: () => void;
            if (hasListChanged) {
                const targetContainerInner = $('.target-container .quick-sl')[0];
                const width = targetContainerInner.offsetWidth;

                targetContainerInner.innerHTML = '';
                targetContainerInner.style.width = `${width}px`;
                targetContainerInner.style.backgroundColor = 'white';

                timeoutFunc = () => {
                    if (clonedQuickSelectionList && clonedQuickSelectionList.parentNode) {
                        clonedQuickSelectionList.parentNode.removeChild(clonedQuickSelectionList);
                        if (quickSLRef.current) {
                            quickSLRef.current.style.display = initialDisplay!;
                        }
                        quickSlInner.classList.add('cover-to-target-full-relative');
                        setTimeout(() => {
                            quickSlInner.classList.remove('cover-to-target-full-relative');
                        }, 1200);
                    }
                };
            } else {
                clonedQuickSelectionList.setAttribute('id', 'animatedQuickSelectionList');
                // clonedQuickSelectionList.firstChild.classList.add("cover-to-target-full");
                clonedQuickSelectionList
                    .getElementsByClassName('quick-sl')[0]
                    .classList.add('cover-to-target-full-relative');

                timeoutFunc = () => {
                    if (clonedQuickSelectionList && clonedQuickSelectionList.parentNode) {
                        clonedQuickSelectionList.parentNode.removeChild(clonedQuickSelectionList);
                        if (quickSLRef.current) {
                            quickSLRef.current.style.display = initialDisplay!;
                        }
                    }
                };
            }
            setTimeout(timeoutFunc, 1600); //wait until animation is finished; then remove cloned element
        }
    }, [activeList?.id, prevActiveList?.id, prevActiveList?.lastModifiedAt, activeList?.lastModifiedAt]);

    return quickSLRef;
};
